import { usePermissions } from '@ltvco/refresh-lib/ctx';
import {
  useLLMSmartSearchV1,
  SmartSearchQueryTypesEnum,
} from '@ltvco/refresh-lib/hooks';
import { commaFormat } from '@ltvco/refresh-lib/utils';
import { useVehicleValuation } from '@ltvco/refresh-lib/v1';
import { useState, useEffect, useCallback, useRef } from 'react';

import { ERROR_MESSAGES } from '../../constants';
import {
  cleanSearchInput,
  determineSearchType,
  extractVehicleParams,
  formatNumericValue,
  type VehicleSearchResult,
  type VehicleSearchType,
  type VehicleValuationParams,
} from '../../utils';
import { buildForecastData } from '../utils';

import { useVehicleReportProcessing } from './useVehicleReportProcessing';

import type { AllSearchesSmartSearchResults } from '@ltvco/refresh-lib/api';

interface UseVehicleForecasterSearchParams {
  onSendMessage?: (message?: string, imageBase64?: string) => void;
  shouldResetInputs: boolean;
  onResetComplete: () => void;
  onProcessingStateChange?: (isProcessing: boolean) => void;
}

interface UseVehicleForecasterSearchReturn {
  searchValue: string;
  setSearchValue: (value: string) => void;
  mileage: string;
  setMileage: (value: string) => void;
  error: string | null;
  hasSubmitted: boolean;
  hasSubmittedMarketValue: boolean;
  searchType: VehicleSearchType;
  showMarketValueInput: boolean;
  marketValue: string;
  handleMileageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMarketValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => Promise<void>;
  handleMarketValueSubmit: (
    e?: React.FormEvent<HTMLFormElement>,
  ) => Promise<void>;
}

/**
 * useVehicleForecasterSearch - Custom hook to manage vehicle forecaster search logic
 * Uses general smart search with context to identify vehicle searches
 * Pattern: fetchedValuation ?? reportValuation (like VehicleMarketAnalysis.tsx)
 * @hook
 */
export const useVehicleForecasterSearch = ({
  onSendMessage,
  shouldResetInputs,
  onResetComplete,
  onProcessingStateChange,
}: UseVehicleForecasterSearchParams): UseVehicleForecasterSearchReturn => {
  const [searchValue, setSearchValue] = useState('');
  const [mileage, setMileage] = useState('');
  const [marketValue, setMarketValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentInputValue, setCurrentInputValue] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasSubmittedMarketValue, setHasSubmittedMarketValue] = useState(false);
  const [searchType, setSearchType] = useState<VehicleSearchType>(null);
  const [enableSearch, setEnableSearch] = useState(false);
  const [valuationParams, setValuationParams] =
    useState<VehicleValuationParams | null>(null);
  const [showMarketValueInput, setShowMarketValueInput] = useState(false);
  const isProcessingRef = useRef(false);

  const { isFreePlan } = usePermissions();
  const {
    data: smartSearchResponse,
    isSuccess,
    isError,
  } = useLLMSmartSearchV1(
    SmartSearchQueryTypesEnum.allReportTypesSmartSearchV1,
    {
      enabled: enableSearch && Boolean(currentInputValue),
      searchValue: currentInputValue,
    },
  );

  const {
    data: fetchedValuation,
    isError: valuationError,
    isSuccess: valuationSuccess,
  } = useVehicleValuation(valuationParams ?? undefined, {
    enabled: Boolean(valuationParams),
  });

  // Reset all form states
  const resetAllStates = useCallback(() => {
    setSearchValue('');
    setMileage('');
    setMarketValue('');
    setError(null);
    setHasSubmitted(false);
    setHasSubmittedMarketValue(false);
    setEnableSearch(false);
    setCurrentInputValue('');
    setSearchType(null);
    setValuationParams(null);
    setShowMarketValueInput(false);
    isProcessingRef.current = false;
    onProcessingStateChange?.(false);
  }, [onProcessingStateChange]);

  const resetInternalState = useCallback(() => {
    setEnableSearch(false);
    setCurrentInputValue('');
    setSearchType(null);
    setValuationParams(null);
    setShowMarketValueInput(false);
    isProcessingRef.current = false;
    onProcessingStateChange?.(false);
  }, [onProcessingStateChange]);

  // Reset internal state AND hasSubmitted when there's an error
  const resetOnError = useCallback(() => {
    resetInternalState();
    setHasSubmitted(false);
  }, [resetInternalState]);

  const { processVehicleReport, pendingData } = useVehicleReportProcessing({
    onSendMessage,
    onProcessingComplete: resetInternalState,
    onProcessingError: resetOnError,
    fetchedValuation,
    setValuationParams,
    setError,
  });

  // Handle valuation error or when valuation returns no data - show market value input
  // Handle reset when shouldResetInputs becomes true
  useEffect(() => {
    if (shouldResetInputs) {
      resetAllStates();
      onResetComplete();
    }
  }, [shouldResetInputs, resetAllStates, onResetComplete]);

  useEffect(() => {
    if (!valuationParams || !pendingData) return;

    // If valuation query completed (success or error) but no market value, show input
    if (
      (valuationSuccess || valuationError) &&
      !fetchedValuation?.clean_retail?.adjusted &&
      !showMarketValueInput
    ) {
      setShowMarketValueInput(true);
      setValuationParams(null);
      setError(null);
      onProcessingStateChange?.(false);
    }
  }, [
    valuationSuccess,
    valuationError,
    valuationParams,
    fetchedValuation,
    pendingData,
    showMarketValueInput,
    onProcessingStateChange,
  ]);

  useEffect(() => {
    if (
      isProcessingRef.current ||
      !isSuccess ||
      !smartSearchResponse ||
      !currentInputValue
    ) {
      return;
    }

    const response = smartSearchResponse as {
      results: AllSearchesSmartSearchResults;
      error?: string;
    };

    if (response.error) {
      setError(ERROR_MESSAGES.TECHNICAL_DIFFICULTIES);
      resetOnError();
      return;
    }

    const vehicleResults = response.results?.filter(
      (r) => r.type === 'vehicle',
    );

    if (!vehicleResults?.length) {
      setError(ERROR_MESSAGES.INVALID_INPUT);
      resetOnError();
      return;
    }

    const firstResult = vehicleResults[0] as VehicleSearchResult;

    setSearchType(determineSearchType(firstResult));

    const vehicleParams = extractVehicleParams(firstResult);
    const cleanMileage = parseInt(commaFormat(mileage).replace(/,/g, ''), 10);

    isProcessingRef.current = true;
    setCurrentInputValue('');
    processVehicleReport.mutate({ vehicleArgs: vehicleParams, cleanMileage });
  }, [
    isSuccess,
    smartSearchResponse,
    currentInputValue,
    mileage,
    processVehicleReport,
    resetOnError,
  ]);

  useEffect(() => {
    if (isError && currentInputValue) {
      setError(ERROR_MESSAGES.TECHNICAL_DIFFICULTIES);
      resetOnError();
    }
  }, [isError, currentInputValue, resetOnError]);

  const handleMileageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMileage(formatNumericValue(e.target.value));
    },
    [],
  );

  const handleSearchValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(cleanSearchInput(e.target.value));
    },
    [],
  );

  const handleMarketValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMarketValue(formatNumericValue(e.target.value));
    },
    [],
  );

  const handleMarketValueSubmit = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>): Promise<void> => {
      e?.preventDefault();

      if (!marketValue || !pendingData || !onSendMessage) return;

      const numericValue = parseFloat(marketValue.replace(/,/g, ''));
      if (isNaN(numericValue) || numericValue <= 0) {
        setError('Please enter a valid market value');
        return;
      }

      setHasSubmittedMarketValue(true);
      setShowMarketValueInput(false);
      onProcessingStateChange?.(true);

      // Build forecast data with manual market value
      const vehicleData = { ...pendingData.vehicleData };
      vehicleData.marketValue = numericValue;
      const message = buildForecastData(
        isFreePlan,
        vehicleData,
        pendingData.cleanMileage,
      );
      onSendMessage(message);
    },
    [marketValue, pendingData, onSendMessage, onProcessingStateChange],
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>): Promise<void> => {
      e?.preventDefault();

      if (!searchValue || !onSendMessage) return;

      setError(null);
      setCurrentInputValue(`Vehicle: ${searchValue}`);
      setHasSubmitted(true);
      setEnableSearch(true);
      onProcessingStateChange?.(true);
    },
    [searchValue, onSendMessage, onProcessingStateChange],
  );

  return {
    searchValue,
    setSearchValue,
    mileage,
    setMileage,
    marketValue,
    error,
    hasSubmitted,
    hasSubmittedMarketValue,
    searchType,
    showMarketValueInput,
    handleMileageChange,
    handleSearchValueChange,
    handleMarketValueChange,
    handleSubmit,
    handleMarketValueSubmit,
  };
};
